import { useEffect, useCallback } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import IdleTimeoutDialog from '../IdleTimeoutDialog';
import { useIdleTimeout } from '../../hooks/useIdleTimeout';
import { useAuthStore } from '../../store/authStore';
import api from '../../lib/api';

const TOKEN_KEY = 'admin_access_token';
const REFRESH_KEY = 'admin_refresh_token';

export default function AdminLayout() {
  const navigate = useNavigate();
  const logout = useAuthStore((s) => s.logout);
  const { showWarning, remainingSeconds, extendSession } = useIdleTimeout();

  const handleIdleLogout = useCallback(() => {
    logout();
    navigate('/login?reason=idle', { replace: true });
  }, [logout, navigate]);

  // Listen for auto-logout event from useIdleTimeout
  useEffect(() => {
    const handler = () => handleIdleLogout();
    window.addEventListener('idle-timeout-logout', handler);
    return () => window.removeEventListener('idle-timeout-logout', handler);
  }, [handleIdleLogout]);

  const handleExtend = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem(REFRESH_KEY);
      const accessToken = localStorage.getItem(TOKEN_KEY);
      if (!refreshToken) {
        handleIdleLogout();
        return;
      }
      const res = await api.post('/v1/auth/refresh', { refreshToken }, accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : undefined);
      localStorage.setItem(TOKEN_KEY, res.data.data.accessToken);
      localStorage.setItem(REFRESH_KEY, res.data.data.refreshToken);
      extendSession();
    } catch {
      handleIdleLogout();
    }
  }, [extendSession, handleIdleLogout]);

  return (
    <div className="flex h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />
        <main className="flex-1 overflow-auto bg-gray-50 p-6">
          <Outlet />
        </main>
      </div>

      {showWarning && (
        <IdleTimeoutDialog
          remainingSeconds={remainingSeconds}
          onExtend={handleExtend}
          onLogout={handleIdleLogout}
        />
      )}
    </div>
  );
}
